import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { WorkspaceRole } from '@crowdctrl/core';
import { WorkspaceMemberService } from './workspace-member.service';
import { finalize, shareReplay, tap } from 'rxjs/operators';

/**
 * Service to cache and manage the current user's workspace role.
 * Follows the same pattern as WorkspaceContextService for consistency.
 *
 * Features:
 * - Observable-based caching for instant navigation
 * - Manual invalidation when roles change
 * - Automatic cache clearing on workspace/user changes
 */
@Injectable({
  providedIn: 'root',
})
export class WorkspaceRoleService {
  private workspaceMemberService = inject(WorkspaceMemberService);

  private roleSubject = new BehaviorSubject<WorkspaceRole | null>(null);
  private cachedWorkspaceId: string | null = null;
  private cachedUserId: string | null = null;
  private loading$: Observable<WorkspaceRole | null> | null = null;

  role$ = this.roleSubject.asObservable();

  /**
   * Get the current cached role value synchronously
   */
  getRoleValue(): WorkspaceRole | null {
    return this.roleSubject.value;
  }

  /**
   * Get the cached workspace ID
   */
  getCachedWorkspaceId(): string | null {
    return this.cachedWorkspaceId;
  }

  /**
   * Get the cached user ID
   */
  getCachedUserId(): string | null {
    return this.cachedUserId;
  }

  /**
   * Ensure the user's workspace role is loaded and cached.
   * Returns cached value if available, fetches from API if not.
   *
   * @param workspaceId The workspace ID
   * @param userId The user's Firebase UID
   * @param forceRefresh Force refresh even if cached
   */
  ensureRole$(
    workspaceId: string,
    userId: string,
    forceRefresh: boolean = false,
  ): Observable<WorkspaceRole | null> {
    // Clear cache if workspace or user changed
    if (this.cachedWorkspaceId !== workspaceId || this.cachedUserId !== userId) {
      this.roleSubject.next(null);
      this.cachedWorkspaceId = null;
      this.cachedUserId = null;
      this.loading$ = null;
    }

    // Return cached if available and not forcing refresh
    if (
      !forceRefresh &&
      this.cachedWorkspaceId === workspaceId &&
      this.cachedUserId === userId &&
      this.roleSubject.value !== null
    ) {
      return of(this.roleSubject.value);
    }

    // Return in-flight request if exists
    if (
      !forceRefresh &&
      this.cachedWorkspaceId === workspaceId &&
      this.cachedUserId === userId &&
      this.loading$
    ) {
      return this.loading$;
    }

    // Update cache keys
    this.cachedWorkspaceId = workspaceId;
    this.cachedUserId = userId;

    // Fetch from API
    const request$ = this.workspaceMemberService.getUserWorkspaceRole(workspaceId, userId).pipe(
      tap((role) => {
        this.roleSubject.next(role);
      }),
      finalize(() => {
        if (this.loading$ === request$) {
          this.loading$ = null;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.loading$ = request$;
    return request$;
  }

  /**
   * Clear all cached role data.
   * Call this when the user switches workspaces or logs out.
   */
  clear(): void {
    this.roleSubject.next(null);
    this.cachedWorkspaceId = null;
    this.cachedUserId = null;
    this.loading$ = null;
  }

  /**
   * Invalidate the cache, forcing a refresh on next access.
   * Call this after role updates, member removals, or other role-affecting operations.
   */
  invalidate(): void {
    this.clear();
  }
}
